import { debounce } from 'lodash';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import { InputWithValidation } from 'src/shared/globals';
import { cartesianProduct, T, uuid, validateInputSecurity } from 'src/shared/helpers';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import AiOutlinePlus from 'src/shared/material_icons/content/add.svg';
import { useValidationContext, validateRequired } from 'src/shared/validation';
import styled from 'styled-components';
import { Button, Link } from '../../../../../widgets/buttons';
import { FormControl } from '../../../../../widgets/formControl';
import { PageFooterButtons } from '../../../../../widgets/headers';
import { Icon } from '../../../../../widgets/icon';
import { ContainerBase } from '../../../../../widgets/layouts';
import { SectionContent } from '../../../../../widgets/section/sectionContent';
import { addNewColorAction, editColorNameAndAttributesAction, removeColorAction, useProductConfigurationState } from '../../../state';
import SelectedOptionValue from '../../../state/selectedOptionValue';
import { WizardFooterBackButton } from '../../wizardFooterBackButton';
import { AttributeContainerItem, AttributeOptionList } from './attributeOptionList';

const VariantsSection = styled(Section)`
    height: calc(100vh - 150px);
    max-width: 1400px;
    margin-left: auto;
    margin-right: auto;
    overflow-y: auto;

    /* necessary to avoid ugly add new attribute sticky animation */
    ${SectionContent} {
        padding-bottom: 0;
    }
`;

const LinkRemoveAttribute = styled(Link)`
    display: flex;
    align-self: start;
    justify-self: end;
    ${Icon} {
        color: ${(props) => props.theme.app.primary};
        svg {
            height: 24px;
            width: 24px;
        }
    }
`;

const LinkAddButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
    height: 52px;
    min-height: 52px;
    flex-shrink: 0;
    box-sizing: border-box;
    position: sticky;
    bottom: 0px;
    z-index: 2;
    background: #fff;

    a {
        display: flex;
        align-items: center;
        text-decoration: none;
        color: ${(props) => props.theme.app.secondary};

        svg {
            margin-right: 5px;
        }
    }
`;

const withTrailingEmptyOption = (list: SelectedOptionValue[][], attributeId: string): SelectedOptionValue[][] => {
    if (list.length === 0) return list;
    const attrIndex = list[0].findIndex((a) => a.Id === attributeId);
    if (attrIndex < 0) return list;

    const options = [...new Set(list.map((m) => JSON.stringify(m[attrIndex])))].map((m2) => JSON.parse(m2) as SelectedOptionValue);
    if (options.some((o) => o.Value.Label.trim() === '')) return list;

    if (list[0].length === 1) {
        return [...list, [{ Id: attributeId, Label: list[0][0].Label, Value: { Id: uuid(), Label: '' } }]];
    }

    const attributesAcc: SelectedOptionValue[][] = [];
    for (let i = 0; i < list[0].length; i++) {
        let elements = [...new Set(list.map((m) => JSON.stringify(m[i])))].map((m2) => JSON.parse(m2) as SelectedOptionValue);
        if (list[0][i].Id === attributeId) elements = [...elements, { Id: attributeId, Label: list[0][i].Label, Value: { Id: uuid(), Label: '' } }];
        attributesAcc.push(elements);
    }
    return cartesianProduct(...attributesAcc);
};

const EditVariantsOffline: FunctionComponent<{ onSaveVariants: () => void }> = ({ onSaveVariants }) => {
    const { state, dispatch, unsavedChanges } = useProductConfigurationState();
    const navigate = useNavigate();
    const validationContext = useValidationContext();

    const modelColorCodes = state.product.colors.map((c) => {
        return JSON.parse(c.code || '[]') as SelectedOptionValue[];
    });

    const [newModelColorCodes, setNewModelColorCodes] = useState<SelectedOptionValue[][]>(() => {
        let initial =
            modelColorCodes[0].length > 0
                ? [...modelColorCodes]
                : modelColorCodes.map((mc) => [...mc, { Id: uuid(), Label: '', Value: { Id: uuid(), Label: '' } }]);
        if (initial.length > 0) {
            initial[0].forEach((attr) => {
                initial = withTrailingEmptyOption(initial, attr.Id);
            });
        }
        return initial;
    });
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const addAttributeLinkRef = useRef<HTMLDivElement>(null);

    const getScroller = (): HTMLElement | null => {
        let node: HTMLElement | null = addAttributeLinkRef.current?.parentElement ?? null;
        while (node) {
            const overflowY = getComputedStyle(node).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') return node;
            node = node.parentElement;
        }
        return null;
    };

    const attributes = newModelColorCodes.length == 0 ? [] : newModelColorCodes[0];

    const handleBackClick = () => {
        if (unsavedChanges.guardNavigation()) return;
        navigate(-1);
    };

    const onAddOption = (attributeId: string) => {
        setNewModelColorCodes(withTrailingEmptyOption([...newModelColorCodes], attributeId));
    };

    const onRemoveOption = (attributeId: string, optionId: string, _optionLabel: string) => {
        let list = [...newModelColorCodes];

        if (list.filter((mc) => mc.some((attr) => attr.Id == attributeId)).length <= 1) return;

        list = list.filter((mc) => !mc.some((attr) => attr.Id == attributeId && attr.Value.Id == optionId));
        if (list.length > 0) setNewModelColorCodes(list);

        let modelColors = [...state.product.colors];
        let newModelColorCodesStringify = list.map((mc) => JSON.stringify(mc));

        modelColors.forEach((c, i) => {
            if (!newModelColorCodesStringify.includes(c.code)) {
                let modelColor = state.product.colors.find((col) => col.code === c.code);
                if (modelColor) dispatch(removeColorAction(modelColor));
            }
        });
    };

    const onEditOption = (newValue: string, attributeId: string, optionId: string) => {
        let list = [...newModelColorCodes];
        list.forEach((mc) => {
            let attribute = mc.find((attr) => attr.Id === attributeId);
            if (attribute && attribute.Value.Id === optionId) attribute.Value.Label = newValue;
        });
        list = withTrailingEmptyOption(list, attributeId);
        setNewModelColorCodes(list);

        let modelColorsCopy = [...state.product.colors];
        list.forEach((mc, i) => {
            if (mc.some((o) => o.Label === '' || o.Value.Label === '')) return;

            let modelColor = modelColorsCopy.find((c) => {
                let modelColorCode = JSON.parse(c.code) as SelectedOptionValue[];
                return modelColorCode.some((o, i) => o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id);
            });

            if (modelColor) {
                let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
            } else {
                dispatch(addNewColorAction(mc));
            }

            modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && mc.guid != modelColor.guid);
        });
    };

    const onEditAttribute = (newAttributeValue: string, attributeId: string) => {
        let list = [...newModelColorCodes];
        list.forEach((mc) => {
            let attribute = mc.find((attr) => attr.Id === attributeId);
            if (attribute) attribute.Label = newAttributeValue;
        });
        setNewModelColorCodes(list);

        let modelColorsCopy = [...state.product.colors];
        list.forEach((mc, i) => {
            if (mc.some((o) => o.Label === '' || o.Value.Label === '')) return;

            let modelColor = modelColorsCopy.find((c) => {
                let modelColorCode = JSON.parse(c.code) as SelectedOptionValue[];
                return modelColorCode.some((o, i) => o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id);
            });

            if (modelColor) {
                let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
            } else {
                dispatch(addNewColorAction(mc));
            }

            modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && mc.guid != modelColor.guid);
        });
    };

    const onAddNewAttribute = () => {
        validationContext.resetSubmitted();

        let list = [...newModelColorCodes];
        list = cartesianProduct(list, [{ Id: uuid(), Label: '', Value: { Id: uuid(), Label: '' } }]);
        setNewModelColorCodes(list);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const scroller = getScroller();
                if (scroller && scroller.scrollHeight > scroller.clientHeight) {
                    scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
                }
            });
        });
    };

    const onRemoveAttribute = (attribute: SelectedOptionValue) => {
        if (newModelColorCodes[0].length == 0) return;

        let list = [...newModelColorCodes];
        let indexAttributeToRemove = attributes.findIndex((a) => a.Id === attribute.Id);

        if (indexAttributeToRemove >= 0) {
            list.forEach((mp) => mp.splice(indexAttributeToRemove, 1));

            list = [...new Set(list.map((m) => JSON.stringify(m)))].map((m2) => JSON.parse(m2));
            setNewModelColorCodes(list);

            let modelColorsCopy = [...state.product.colors];
            list.forEach((mc, i) => {
                let modelColor = modelColorsCopy.find((c) => {
                    let modelColorCode = (JSON.parse(c.code) || []) as SelectedOptionValue[];
                    if (modelColorCode.length == 0) return false;

                    if (modelColorCode.length != mc.length)
                        return modelColorCode.some((o, i) => {
                            if (i >= mc.length) return true;
                            return o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id;
                        });

                    return false;
                });

                if (modelColor) {
                    let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                    dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
                    modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && modelColor.guid != mc.guid);
                }
            });

            let newModelColorsState = [...state.product.colors];

            if (attribute.Label != '') {
                modelColorsCopy.forEach((mc) => {
                    let modelColorCode = (JSON.parse(mc.code) || []) as SelectedOptionValue[];
                    if (modelColorCode.length >= list[0].length) {
                        let modelColorToRemove = newModelColorsState.find((c) => c.code === mc.code);

                        if (modelColorToRemove) dispatch(removeColorAction(modelColorToRemove));

                        newModelColorsState = newModelColorsState.filter((mc) => modelColorToRemove && mc.guid != modelColorToRemove.guid);
                    }
                });
            }
        }
    };

    const onConfirmAndSaveVariants = () => {
        setIsFirstLoad(false);

        const allAttributesValid = attributes.every((attr, i) => {
            const nameOk = (attr.Label ?? '').trim() !== '' && validateInputSecurity(attr.Label).isValid;
            const hasFilledOption = newModelColorCodes.some((row) => {
                const label = row[i]?.Value.Label ?? '';
                return label.trim() !== '' && validateInputSecurity(label).isValid;
            });
            return nameOk && hasFilledOption;
        });

        if (!allAttributesValid) {
            validationContext.submit();
            return;
        }

        onSaveVariants();
    };

    const onChangeAttributeValue = debounce(onEditAttribute, 10);
    const handleInputAttributeChange = (e: React.ChangeEvent<HTMLInputElement>, attributeId: string) => {
        onChangeAttributeValue(e.currentTarget.value, attributeId);
    };

    return (
        <ContainerBase>
            <VariantsSection>
                {attributes.length > 0 &&
                    attributes.map((attribute, index) => {
                        const hasFilledOption = newModelColorCodes.some((row) => (row[index]?.Value.Label ?? '').trim() !== '');
                        return (
                            <AttributeContainerItem itemID={attribute.Id} key={`Attribute-${index}`}>
                                <FormControl label='Attribute name' caption={T._('Enter your attribute title, such as "color" or "size"', 'Admin')}>
                                    <InputWithValidation
                                        message={`${!validateInputSecurity(attribute.Label).isValid ? T._(`Invalid characters detected: ${validateInputSecurity(attribute.Label).invalidCharacters.join(', ')}`, 'Admin') : validateRequired(attribute.Label, true) ? T._('Please enter the attribute name', 'Admin') : ''}`}
                                        value={attribute.Label}
                                        onChange={(e) => handleInputAttributeChange(e, attribute.Id)}
                                        placeholder={T._('Attribute name', 'Admin')}
                                        validationKey={`AttributeValidation-${attribute.Id}-${index}`}
                                        validate={(value) => validateRequired(value, true) || !validateInputSecurity(value).isValid}
                                    />
                                </FormControl>
                                <FormControl
                                    label={T._('Attribute values', 'Admin')}
                                    caption={T._('Enter the options for the attribute, such as "red" for "color" or "medium" for "size"', 'Admin')}
                                >
                                    <AttributeOptionList
                                        isFirstLoad={isFirstLoad}
                                        isAddVisible={false}
                                        requireOption={!hasFilledOption}
                                        onRemoveOption={onRemoveOption}
                                        onEditOption={onEditOption}
                                        onAddOption={onAddOption}
                                        values={[...new Set(newModelColorCodes.map((m) => JSON.stringify(m[index])))].map((m2) => JSON.parse(m2))}
                                    />
                                </FormControl>
                                {newModelColorCodes[0].length > 1 && (
                                    <LinkRemoveAttribute onClick={() => onRemoveAttribute(attribute)}>
                                        <Icon>
                                            <TrashIcon />
                                        </Icon>
                                    </LinkRemoveAttribute>
                                )}
                            </AttributeContainerItem>
                        );
                    })}

                <LinkAddButtonContainer ref={addAttributeLinkRef}>
                    <Link onClick={onAddNewAttribute}>
                        <Icon>
                            <AiOutlinePlus></AiOutlinePlus>
                        </Icon>
                        {T._('Add new attribute', 'Admin')}
                    </Link>
                </LinkAddButtonContainer>

                <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={handleBackClick} />}>
                    <Button onClick={onConfirmAndSaveVariants}>{T._('Apply', 'Admin')}</Button>
                </PageFooterButtons>
            </VariantsSection>
        </ContainerBase>
    );
};

export default EditVariantsOffline;
